// Behaviour form helpers — multi-regulation visibility mapping.

import {
  CATCH_ALL_COVERAGE_MESSAGE,
  CATCH_ALL_OWNER,
  DEFAULT_LOCALE_CHOICE_MESSAGE,
  REGULATION_CARDS,
  REGULATION_PROTECTION_ORDER,
  TERRITORY_REGULATION_CARDS,
  UNSET_CONFIGURE_CHOICES_MESSAGE,
  VISIBLE_COUNTRIES_ALL,
  VISIBLE_COUNTRIES_DEFAULT,
} from 'src/services/consts/banner/behaviour.const'
import { SUPPORTED_LOCALES } from 'src/services/consts/banner/create.const'
import { BANNER_REGULATION } from 'src/services/consts/shared.const'
import { isValidUrl } from 'src/services/helpers/security.helper'

/**
 * @typedef {Object} VisibilityFormValues
 * @property {string} catchAllOwner - `CATCH_ALL_OWNER.NONE` or a `BANNER_REGULATION` value
 */

/**
 * @typedef {Object} BehaviourFormValues
 * @property {string} privacyPolicyUrl
 * @property {string|null} defaultLocale
 * @property {string[]} availableLocales - checked locale codes; never the “all languages” wire value `[]`
 * @property {boolean} googleConsentMode
 * @property {string[]} regulations
 * @property {string} catchAllOwner
 */

const SUPPORTED_LOCALE_CODES = SUPPORTED_LOCALES.map((locale) => locale.value)

/**
 * Languages selected in the form for a saved `availableLocales`.
 * `null`, a missing value, `[]`, and a list with no known codes all mean every language.
 * Drops unknown codes, the select-all token, and duplicates. Returns a new array.
 * @param {string[]|null|undefined} savedAvailableLocales
 * @returns {string[]}
 */
export function toFormAvailableLocales(savedAvailableLocales) {
  const seenLanguageCodes = new Set()
  const selectedLanguageCodes = []
  if (Array.isArray(savedAvailableLocales)) {
    for (const rawLanguageCode of savedAvailableLocales) {
      if (typeof rawLanguageCode !== 'string') continue
      const languageCode = rawLanguageCode.toLowerCase()
      if (!SUPPORTED_LOCALE_CODES.includes(languageCode) || seenLanguageCodes.has(languageCode)) {
        continue
      }
      seenLanguageCodes.add(languageCode)
      selectedLanguageCodes.push(languageCode)
    }
  }
  if (selectedLanguageCodes.length === 0) return [...SUPPORTED_LOCALE_CODES]
  return selectedLanguageCodes
}

/**
 * `availableLocales` sent to the API. `[]` means every language, including ones added later.
 * An empty form selection is not that value.
 * @param {string[]|null|undefined} selectedLanguageCodes
 * @returns {string[]}
 */
export function toApiAvailableLocales(selectedLanguageCodes) {
  if (!Array.isArray(selectedLanguageCodes) || selectedLanguageCodes.length === 0) {
    throw new Error('toApiAvailableLocales: an empty selection cannot mean every language')
  }
  const selectedLanguageCodeSet = new Set(selectedLanguageCodes)
  const isEveryLanguageSelected =
    selectedLanguageCodeSet.size === SUPPORTED_LOCALE_CODES.length &&
    SUPPORTED_LOCALE_CODES.every((languageCode) => selectedLanguageCodeSet.has(languageCode))
  if (isEveryLanguageSelected) return []
  return [...selectedLanguageCodes]
}

/**
 * True when at least one language is selected and the default language is one of them.
 * @param {{
 *   availableLocales: string[]|null|undefined,
 *   defaultLocale: string|null|undefined,
 * }} params
 * @returns {boolean}
 */
export function isDefaultLocaleSelected({ availableLocales, defaultLocale }) {
  return (
    Array.isArray(availableLocales) &&
    availableLocales.length > 0 &&
    availableLocales.includes(defaultLocale)
  )
}

/**
 * Territory regulation values currently selected (strips notice if present).
 * @param {string[]|null|undefined} regulations
 * @returns {string[]}
 */
export function getCheckedTerritoryRegulations(regulations) {
  const selected = Array.isArray(regulations) ? regulations : []
  return TERRITORY_REGULATION_CARDS.filter((card) => selected.includes(card.value)).map(
    (card) => card.value
  )
}

/**
 * Privacy-policy URL is optional. A blank value is allowed; anything typed must be a real URL.
 * @param {string|null|undefined} privacyPolicyUrl
 * @returns {boolean}
 */
export function isPrivacyPolicyUrlValid(privacyPolicyUrl) {
  const url = privacyPolicyUrl?.trim() ?? ''
  if (!url) return true
  return isValidUrl(url)
}

/**
 * True when Rest of the World already has a still-valid owner.
 * Notice is an owner, not a territory. `NONE` is valid only while a territory remains.
 * @param {{
 *   regulations: string[]|null|undefined,
 *   catchAllOwner: string|null|undefined,
 * }} params
 * @returns {boolean}
 */
export function isCatchAllOwnerChosen({ regulations, catchAllOwner }) {
  if (catchAllOwner == null) return false
  if (catchAllOwner === BANNER_REGULATION.NOTICE) return true

  const territories = getCheckedTerritoryRegulations(regulations)
  if (territories.includes(catchAllOwner)) return true
  return catchAllOwner === CATCH_ALL_OWNER.NONE && territories.length > 0
}

/**
 * Keep a still-valid Rest of the World owner after a regulation card toggle.
 * Notice in the card list is an explicit Rest of the World choice.
 * Does not fall back to the most protective law.
 * @param {{
 *   regulations: string[]|null|undefined,
 *   catchAllOwner: string|null|undefined,
 * }} params
 * @returns {string|null}
 */
export function resolveCatchAllOwnerAfterCardToggle({ regulations, catchAllOwner }) {
  const selected = Array.isArray(regulations) ? regulations : []
  if (selected.includes(BANNER_REGULATION.NOTICE)) {
    return BANNER_REGULATION.NOTICE
  }

  const territories = getCheckedTerritoryRegulations(selected)
  if (territories.includes(catchAllOwner)) return catchAllOwner
  if (catchAllOwner === CATCH_ALL_OWNER.NONE && territories.length > 0) {
    return CATCH_ALL_OWNER.NONE
  }
  return null
}

/**
 * Keep a still-valid Rest of the World owner after the selected regulations change.
 * Returns null when the current owner no longer applies.
 * Pure — never mutates inputs.
 * @param {{
 *   regulations: string[]|null|undefined,
 *   catchAllOwner: string|null|undefined,
 * }} params
 * @returns {string|null}
 */
export function resolveCatchAllOwnerAfterRegulationChange({ regulations, catchAllOwner }) {
  if (isCatchAllOwnerChosen({ regulations, catchAllOwner })) return catchAllOwner
  return null
}

/**
 * Keep the current default language only while it is still selected.
 * Does not pick another language. Pure — never mutates inputs.
 * @param {{
 *   availableLocales: string[]|null|undefined,
 *   defaultLocale: string|null|undefined,
 * }} params
 * @returns {string|null}
 */
export function resolveDefaultLocaleAfterAvailableLocalesChange({
  availableLocales,
  defaultLocale,
}) {
  if (Array.isArray(availableLocales) && availableLocales.includes(defaultLocale)) {
    return defaultLocale
  }
  return null
}

/**
 * Why Publish or Create stays disabled when a configure choice is unset.
 * Privacy URL is not part of this message.
 * @param {{
 *   availableLocales: string[]|null|undefined,
 *   defaultLocale: string|null|undefined,
 *   regulations: string[]|null|undefined,
 *   catchAllOwner: string|null|undefined,
 * }} params
 * @returns {string|null}
 */
export function getUnsetConfigureChoiceMessage({
  availableLocales,
  defaultLocale,
  regulations,
  catchAllOwner,
}) {
  const hasDefaultLocale = isDefaultLocaleSelected({ availableLocales, defaultLocale })
  const hasCatchAllOwner = isCatchAllOwnerChosen({ regulations, catchAllOwner })
  if (!hasDefaultLocale && !hasCatchAllOwner) return UNSET_CONFIGURE_CHOICES_MESSAGE
  if (!hasDefaultLocale) return DEFAULT_LOCALE_CHOICE_MESSAGE
  if (!hasCatchAllOwner) return CATCH_ALL_COVERAGE_MESSAGE.CHOOSE
  return null
}

/**
 * Display label for a regulation value.
 * @param {string} value
 * @returns {string}
 */
export function getRegulationDisplayLabel(value) {
  const card = REGULATION_CARDS.find((item) => item.value === value)
  if (!card) return value
  return `${card.longLabel} - ${card.region}`
}

/**
 * Most-protective regulation within a selection, per `REGULATION_PROTECTION_ORDER`.
 * Falls back to GDPR so callers always receive a renderable regulation.
 * @param {string[]|null|undefined} regulations
 * @returns {string}
 */
export function getMostProtectiveRegulation(regulations) {
  const selected = Array.isArray(regulations) ? regulations : []
  return (
    REGULATION_PROTECTION_ORDER.find((regulation) => selected.includes(regulation)) ??
    BANNER_REGULATION.GDPR
  )
}

/**
 * True when the banner collects no consent choices — exactly one regulation and it is notice.
 * @param {string[]|null|undefined} regulations
 * @returns {boolean}
 */
export function isNoticeOnlyBanner(regulations) {
  return (
    Array.isArray(regulations) &&
    regulations.length === 1 &&
    regulations[0] === BANNER_REGULATION.NOTICE
  )
}

/**
 * Default catch-all owner from the current regulation selection.
 * - empty/nullish -> NONE
 * - exactly 1 selected -> that regulation
 * - 2+ including notice -> notice (it has no region of its own, so it can only be the fallback)
 * - otherwise (e.g. GDPR + CCPA) -> the most protective selected regulation
 *   Same "when unsure, default to the safer option" reasoning as the widget's own
 *   conservative-first boot posture — under-covering unclaimed visitors is the
 *   riskier default for a compliance product to ship, not the safer one.
 * @param {string[]|null|undefined} regulations
 * @returns {string}
 */
export function getDefaultCatchAllOwner(regulations) {
  if (!Array.isArray(regulations) || regulations.length === 0) {
    return CATCH_ALL_OWNER.NONE
  }
  if (regulations.length === 1) {
    return regulations[0]
  }
  if (regulations.includes(BANNER_REGULATION.NOTICE)) {
    return BANNER_REGULATION.NOTICE
  }
  return getMostProtectiveRegulation(regulations)
}

/**
 * Enforces the notice invariant: `notice ∈ regulations ⟺ catchAllOwner === notice`.
 * Strips notice, then re-appends it only when it owns the catch-all.
 * Pure — never mutates inputs.
 * @param {{
 *   regulations: string[]|null|undefined,
 *   catchAllOwner: string,
 * }} params
 * @returns {{ regulations: string[], catchAllOwner: string }}
 */
export function applyCatchAllOwner({ regulations, catchAllOwner }) {
  const withoutNotice = (Array.isArray(regulations) ? regulations : []).filter(
    (regulation) => regulation !== BANNER_REGULATION.NOTICE
  )
  const nextRegulations =
    catchAllOwner === BANNER_REGULATION.NOTICE
      ? [...withoutNotice, BANNER_REGULATION.NOTICE]
      : withoutNotice

  return { regulations: nextRegulations, catchAllOwner }
}

/**
 * Maps UI visibility form values to the wire `visibleCountries` object map.
 * Exactly one key per selected regulation; the catch-all owner gets `['*']`,
 * every other selected regulation gets `['default']`.
 * When owner is NONE (or not in `regulations`), no key gets `'*'`.
 * @param {{
 *   regulations: string[]|null|undefined,
 *   catchAllOwner: string,
 * }} params
 * @returns {Record<string, string[]>}
 */
export function mapVisibilityFormToVisibleCountries({ regulations, catchAllOwner }) {
  /** @type {Record<string, string[]>} */
  const map = {}
  if (!Array.isArray(regulations)) return map

  const ownerInSelection =
    catchAllOwner !== CATCH_ALL_OWNER.NONE && regulations.includes(catchAllOwner)

  for (const regulation of regulations) {
    if (ownerInSelection && regulation === catchAllOwner) {
      map[regulation] = [...VISIBLE_COUNTRIES_ALL]
      continue
    }
    map[regulation] = [...VISIBLE_COUNTRIES_DEFAULT]
  }
  return map
}

/**
 * Maps a migrated object-map `visibleCountries` into UI visibility form values.
 * Only the catch-all owner is extracted; custom lists are impossible under the
 * sentinel-only model, so everything else is re-derived from `regulations`.
 * @param {Record<string, string[]>|null|undefined} visibleCountries
 * @param {string[]|null|undefined} regulations
 * @returns {VisibilityFormValues}
 */
export function mapVisibleCountriesToVisibilityForm(visibleCountries, regulations) {
  const selected = Array.isArray(regulations) ? regulations : []

  if (
    visibleCountries &&
    typeof visibleCountries === 'object' &&
    !Array.isArray(visibleCountries)
  ) {
    for (const regulation of selected) {
      if (!(regulation in visibleCountries)) continue
      const countries = visibleCountries[regulation]
      if (
        Array.isArray(countries) &&
        countries.length === 1 &&
        countries[0] === VISIBLE_COUNTRIES_ALL[0]
      ) {
        return { catchAllOwner: regulation }
      }
    }
  }

  return { catchAllOwner: CATCH_ALL_OWNER.NONE }
}

/**
 * Order-insensitive string-list equality (regulations, availableLocales).
 * @param {string[]|null|undefined} a
 * @param {string[]|null|undefined} b
 * @returns {boolean}
 */
function areUnorderedStringListsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((value, index) => value === sortedB[index])
}

/**
 * Field-by-field equality check for two cloned behaviour form objects.
 * @param {BehaviourFormValues} a
 * @param {BehaviourFormValues} b
 * @returns {boolean}
 */
export function isBehaviourEqual(a, b) {
  return (
    a.privacyPolicyUrl === b.privacyPolicyUrl &&
    a.defaultLocale === b.defaultLocale &&
    a.googleConsentMode === b.googleConsentMode &&
    areUnorderedStringListsEqual(a.regulations, b.regulations) &&
    areUnorderedStringListsEqual(a.availableLocales, b.availableLocales) &&
    a.catchAllOwner === b.catchAllOwner
  )
}

/**
 * Empty behaviour form for create / pre-hydrate placeholders.
 * Catch-all ownership is derived here once — not re-derived in clone paths.
 * @returns {BehaviourFormValues}
 */
export function getDefaultBehaviourValues() {
  const regulations = [BANNER_REGULATION.GDPR]
  return {
    privacyPolicyUrl: '',
    defaultLocale: 'en',
    availableLocales: [...SUPPORTED_LOCALE_CODES],
    googleConsentMode: false,
    regulations,
    catchAllOwner: getDefaultCatchAllOwner(regulations),
  }
}

/**
 * Map a resolved `BannerDetail` into behaviour form values.
 * Coerces nullable detail fields into form-safe primitives only.
 * Trusts `catchAllOwner` from `normalizeBannerDetail` — does not invent regulations
 * or re-run `getDefaultCatchAllOwner`.
 * @param {import('./detail.helper').BannerDetail} detail
 * @returns {BehaviourFormValues}
 */
export function toBehaviourFormValues(detail) {
  return {
    privacyPolicyUrl: detail.privacyPolicyUrl ?? '',
    defaultLocale: detail.defaultLocale ?? 'en',
    availableLocales: toFormAvailableLocales(detail.availableLocales),
    googleConsentMode: detail.googleConsentMode ?? false,
    regulations: [...detail.regulations],
    catchAllOwner: detail.catchAllOwner,
  }
}

/**
 * Structural clone of behaviour form values. No defaults, no catch-all derivation.
 * @param {BehaviourFormValues} source
 * @returns {BehaviourFormValues}
 */
export function cloneBehaviourValues(source) {
  return {
    ...source,
    regulations: [...source.regulations],
    availableLocales: [...source.availableLocales],
  }
}
